/****************************************************************************************
 * File Name    : ErrorList.vue
 * Function     : 중점관리 현황 페이지
 * Author       : 최명호
 * Tester       :
 * Page         :
 * Target       :
 * Description  : 중점관리 등록에서 등록된 데이터들을 목록형태로 화면에 표출하는 페이지
 * Modification Log
 * ======================================================================================
 * Ver  Date        	Author     	Modification
 * ======================================================================================
   1.0  2021.04.16  	최명호    	Create
****************************************************************************************/
<template>
    <div>
        <a-row :gutter="16">
            <a-col class="gutter-row" :span="12">
                <div class="gutter-box">
                    <PageHeader title="등록현황" />
                </div>
            </a-col>
        </a-row>
        <a-form class="" layout="vertical">
            <a-card>
                <a-space align="end">
                    조회
                    <a-input default-value="" v-model="filterCS" @input="debounceFilterFocusList" placeholder="검색어 입력">	
                        <a-select slot="addonAfter" default-value="모델명" v-model="filterField" @change="filterFocusList">	
                            <a-select-option value="모델명">모델명</a-select-option>	
                            <a-select-option value="제품명">제품명</a-select-option>
                        </a-select>	
                    </a-input>

                    일자 조회
                    <a-date-picker 
                        v-model="filterDateStartCS" 
                        @change="filterFocusList" 
                        format="YYYY-MM-DD"
                        placeholder="시작일"
                        style="width:120px;"
                    />
                    ~
                    <a-date-picker 
                        v-model="filterDateEndCS" 
                        @change="filterFocusList" 
                        format="YYYY-MM-DD"
                        placeholder="종료일"
                        style="width:120px;"
                    />

                    <a-divider type="vertical" />
                    <div class="border:1px solid red;width:100%">
                        <a-button type="primary" icon="file-excel" size="default" @click="downloadExcel">
                            엑셀 다운로드
                        </a-button>
                    </div>
                </a-space>
                <p/>
                
                <!-- 중점관리 항목 그리드 -->
                <div class="focus-grid">
                    <div v-for="(item, index) in paginatedItems" :key="index" class="focus-item">
                        <div class="focus-card">
                            <div class="focus-header">
                                <div class="focus-title">{{ item.productInfoText }}</div>
                                <div class="focus-actions">
                                    <a-button type="link" size="small" @click="handleEdit(item.seq)">
                                        <a-icon type="edit" /> Edit
                                    </a-button>
                                    <a-button type="link" size="small" class="delete-btn" @click="handleDelete(item.seq)">
                                        <a-icon type="delete" /> Delete
                                    </a-button>
                                </div>
                            </div>
                            
                            <div class="focus-images">
                                <img v-for="(imageId, imgIndex) in item.imageIds" 
                                     :key="imgIndex" 
                                     :src="getImageUrl(imageId)" 
                                     class="focus-image"
                                     @click="showLargeImage(imageId)">
                            </div>
                            
                            <div class="focus-info">
                                <div class="info-row">
                                    <div class="info-label">등록일</div>
                                    <div class="info-value">{{ formatDate(item.created) }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- 페이지네이션 -->
                <div class="pagination-container">
                    <a-pagination 
                        :current="currentPage" 
                        :pageSize="pageSize" 
                        :total="totalItems" 
                        @change="handlePageChange"
                    />
                </div>
            </a-card>
        </a-form>
        
        <!-- 이미지 확대 모달 -->
        <a-modal
            :visible="imageModalVisible"
            :footer="null"
            @cancel="closeImageModal"
            width="80%"
            centered
        >
            <img :src="largeImageUrl" style="width: 100%" />
        </a-modal>
    </div>
</template>

<script>
import _ from "lodash";
import PageHeader from "../components/PageHeader.vue";
import ExcelJS from "exceljs/dist/exceljs";

export default {
    name: 'ErrorList',
    components: {
        PageHeader
    },
    data() {
        return {
            focusItems: [],
            filteredItems: [],
            allProducts: [],
            currentPage: 1,
            pageSize: 4,
            totalItems: 0,
            imageModalVisible: false,
            largeImageUrl: '',
            filterCS: '',
            filterField: '모델명',
            filterDateStartCS: '',
            filterDateEndCS: '',
            debounceTimer: null
        };
    },
    mounted() {
        this.fetchFocusList();
        this.fetchProductList();
    },
    methods: {
        // 중점관리 목록 가져오기
        fetchFocusList() {
            const url = this.$store.state.nodeApiUrl + '/focus/focuslist';
            this.$http.get(url)
                .then((response) => {
                    this.processFocusData(response.data);
                })
                .catch((error) => {
                    console.error('중점관리 목록 조회 실패:', error);
                    this.$message.error('중점관리 목록을 가져오는 중 오류가 발생했습니다.');
                });
        },
        
        // 전체 제품 목록 가져오기
        fetchProductList() {
            const url = this.$store.state.nodeApiUrl + '/product/list?size=1000';
            this.$http.get(url)
                .then((response) => {
                    if (response.data.code === 'success') {
                        this.allProducts = response.data.data.content;
                        // 제품 정보가 로드되면 중점관리 데이터를 다시 처리
                        if (this.focusItems.length > 0) {
                            this.updateProductInfo();
                        }
                    }
                })
                .catch((error) => {
                    console.error('제품 목록 조회 실패:', error);
                    this.$message.error('제품 목록을 가져오는 중 오류가 발생했습니다.');
                });
        },
        
        // 중점관리 데이터 처리
        processFocusData(data) {
            let processedItems = [];
            
            // 각 중점관리 항목 처리
            data.forEach((item) => {
                const productSeqList = item.PRODUCT_SEQ_LIST ? item.PRODUCT_SEQ_LIST.split('||') : [];
                const imageSeqList = item.UPLOAD_FILE_SEQ_LIST ? item.UPLOAD_FILE_SEQ_LIST.split('||') : [];
                
                // 모든 제품 정보 수집
                const productInfos = [];
                productSeqList.forEach(seq => {
                    const productSeq = parseInt(seq);
                    const productInfo = this.getProductInfo(productSeq);
                    if (productInfo) {
                        productInfos.push({
                            modelName: productInfo.productModel || '',
                            modelDetail: productInfo.productModelDetail || '',
                            productName: productInfo.productName || '',
                            pop: productInfo.productPop || '',
                            sap: productInfo.productSap || ''
                        });
                    }
                });
                
                // 제품 정보 텍스트 생성
                const productInfoText = this.createProductInfoText(productInfos);
                
                // 처리된 중점관리 항목 객체 생성
                const processedItem = {
                    seq: item.SEQ,
                    productSeqs: productSeqList.map(seq => parseInt(seq)),
                    imageIds: imageSeqList,
                    created: item.CREATED,
                    updated: item.UPDATED,
                    productInfos: productInfos,
                    productInfoText: productInfoText
                };
                
                processedItems.push(processedItem);
            });
            
            this.focusItems = processedItems;
            this.filteredItems = [...processedItems];
            this.totalItems = processedItems.length;
        },
        
        // 제품 정보 텍스트 생성
        createProductInfoText(productInfos) {
            if (!productInfos || productInfos.length === 0) {
                return '제품 정보 없음';
            }
            
            return productInfos.map(info => {
                const parts = [];
                if (info.modelName) parts.push(info.modelName);
                if (info.modelDetail) parts.push(info.modelDetail);
                if (info.productName) parts.push(info.productName);
                return parts.join(' ');
            }).join(', ');
        },
        
        // 제품 정보 업데이트
        updateProductInfo() {
            this.focusItems = this.focusItems.map(item => {
                const productInfos = [];
                item.productSeqs.forEach(productSeq => {
                    const productInfo = this.getProductInfo(productSeq);
                    if (productInfo) {
                        productInfos.push({
                            modelName: productInfo.productModel || '',
                            modelDetail: productInfo.productModelDetail || '',
                            productName: productInfo.productName || '',
                            pop: productInfo.productPop || '',
                            sap: productInfo.productSap || ''
                        });
                    }
                });
                
                const productInfoText = this.createProductInfoText(productInfos);
                
                return {
                    ...item,
                    productInfos: productInfos,
                    productInfoText: productInfoText
                };
            });
            
            this.filteredItems = [...this.focusItems];
            this.totalItems = this.filteredItems.length;
        },
        
        // 제품 정보 가져오기
        getProductInfo(productSeq) {
            if (!productSeq || !this.allProducts.length) return null;
            return this.allProducts.find(product => product.productSeq === productSeq);
        },
        
        // 이미지 URL 생성
        getImageUrl(imageId) {
            return this.$store.state.hostInService + '/upload/image/download/' + imageId;
        },
        
        // 날짜 포맷
        formatDate(dateStr) {
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        
        // 페이지 변경 처리
        handlePageChange(page) {
            this.currentPage = page;
        },
        
        // 이미지 확대 보기
        showLargeImage(imageId) {
            this.largeImageUrl = this.getImageUrl(imageId);
            this.imageModalVisible = true;
        },
        
        // 이미지 모달 닫기
        closeImageModal() {
            this.imageModalVisible = false;
        },
        
        // Edit 버튼 핸들러
        handleEdit(seq) {
            window.location.hash= `/errorCreate/${seq}`;
        },
        
        // Delete 버튼 핸들러
        handleDelete(seq) {
            if (confirm('정말 삭제하시겠습니까?')) {
                const url = `${this.$store.state.nodeApiUrl}/focus/focusdelete/${seq}`;
                this.$http.delete(url)
                    .then(() => {
                        this.$message.success('성공적으로 삭제되었습니다.');
                        // 목록 다시 불러오기
                        this.fetchFocusList();
                    })
                    .catch((error) => {
                        console.error('삭제 중 오류 발생:', error);
                        this.$message.error('삭제 중 오류가 발생했습니다.');
                    });
            }
        },
        
        // 디바운스 필터링 (입력이 끝난 후 300ms 후에 필터링 실행)
        debounceFilterFocusList() {
            // 이전 타이머가 있다면 취소
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            
            // 300ms 후에 필터링 실행
            this.debounceTimer = setTimeout(() => {
                this.filterFocusList();
            }, 300);
        },
        
        // 필터링 처리
        filterFocusList() {
            console.log('필터링 실행:', this.filterCS, this.filterField, this.filterDateStartCS, this.filterDateEndCS);
            
            let tempItems = [...this.focusItems];
            
            // 검색어로 필터링
            if (this.filterCS && this.filterCS.trim() !== '') {
                const searchText = this.filterCS.toLowerCase().trim();
                if (this.filterField === '모델명') {
                    tempItems = tempItems.filter(item => 
                        item.productInfos.some(info => 
                            (info.modelName && info.modelName.toLowerCase().includes(searchText)) ||
                            (info.modelDetail && info.modelDetail.toLowerCase().includes(searchText))
                        )
                    );
                } else if (this.filterField === '제품명') {
                    tempItems = tempItems.filter(item => 
                        item.productInfos.some(info => 
                            (info.productName && info.productName.toLowerCase().includes(searchText))
                        )
                    );
                }
            }
            
            // 시작 날짜로 필터링
            if (this.filterDateStartCS) {
                // DatePicker는 이미 Date 객체를 반환하므로 변환 필요 없음
                const startDateObj = new Date(this.filterDateStartCS);
                
                // 시작일의 시작 시간(00:00:00)으로 설정
                startDateObj.setHours(0, 0, 0, 0);
                
                console.log('시작일 필터링:', startDateObj);
                
                tempItems = tempItems.filter(item => {
                    const itemDate = new Date(item.created);
                    // 날짜만 비교하기 위해 시간 부분을 제거
                    itemDate.setHours(0, 0, 0, 0);
                    return itemDate >= startDateObj;
                });
            }
            
            // 종료 날짜로 필터링
            if (this.filterDateEndCS) {
                // DatePicker는 이미 Date 객체를 반환하므로 변환 필요 없음
                const endDateObj = new Date(this.filterDateEndCS);
                
                // 종료일의 끝 시간(23:59:59.999)으로 설정
                endDateObj.setHours(23, 59, 59, 999);
                
                console.log('종료일 필터링:', endDateObj);
                
                tempItems = tempItems.filter(item => {
                    const itemDate = new Date(item.created);
                    return itemDate <= endDateObj;
                });
            }
            
            this.filteredItems = tempItems;
            this.totalItems = tempItems.length;
            this.currentPage = 1; // 필터링 시 첫 페이지로 돌아가기
            
            console.log('필터링 결과:', this.filteredItems.length, '개 항목');
        },
        
        // 엑셀 다운로드
        downloadExcel() {
            try {
                this.excelDownload();
            } catch (e) {
                console.error('엑셀 다운로드 오류:', e);
                this.$message.error('엑셀 다운로드 중 오류가 발생했습니다.');
            }
        },
        
        // 엑셀 파일 생성 및 다운로드
        async excelDownload() {
            // 새로운 워크북 생성
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('중점관리 현황');

            // 헤더 설정
            worksheet.columns = [
                { header: '번호', key: 'seq', width: 10 },
                { header: '제품정보', key: 'productInfo', width: 50 },
                { header: '등록일', key: 'created', width: 15 },
                { header: '마지막 수정일', key: 'updated', width: 15 }
            ];

            // 스타일 설정
            worksheet.getRow(1).font = { bold: true };
            worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

            // 데이터 준비 (필터링된 항목을 사용)
            const sortedItems = _.sortBy(this.filteredItems, 'created');
            
            // 데이터 추가
            sortedItems.forEach((item) => {
                worksheet.addRow({
                    seq: item.seq,
                    productInfo: item.productInfoText,
                    created: this.formatDate(item.created),
                    updated: this.formatDate(item.updated)
                });
            });

            // 모든 셀 테두리 설정
            worksheet.eachRow((row, rowNumber) => {
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                    // 데이터 행의 정렬 설정
                    if (rowNumber > 1) {
                        cell.alignment = { vertical: 'middle', horizontal: 'left' };
                    }
                });
            });

            try {
                // 엑셀 파일 생성
                const buffer = await workbook.xlsx.writeBuffer();
                
                // Blob 생성
                const blob = new Blob([buffer], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                
                // 다운로드 링크 생성
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `중점관리현황_${new Date().toISOString().split('T')[0]}.xlsx`;
                
                // 다운로드 실행
                document.body.appendChild(link);
                link.click();
                
                // cleanup
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                this.$message.success('엑셀 파일이 다운로드되었습니다.');
            } catch (error) {
                console.error('Excel file generation failed:', error);
                this.$message.error('엑셀 파일 생성에 실패했습니다.');
            }
        }
    },
    computed: {
        // 현재 페이지에 표시할 아이템
        paginatedItems() {
            const startIndex = (this.currentPage - 1) * this.pageSize;
            return this.filteredItems.slice(startIndex, startIndex + this.pageSize);
        }
    }
}
</script>

<style scoped>
.focus-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 20px;
    margin-top: 20px;
}

.focus-item {
    width: 100%;
}

.focus-card {
    height: 100%;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    padding: 15px;
    background-color: #fff;
}

.focus-header {
    margin-bottom: 15px;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.focus-title {
    font-size: 16px;
    margin: 0 0 5px 0;
    color: var(--primary-color);
    line-height: 1.4;
    flex: 1;
}

.focus-actions {
    white-space: nowrap;
    margin-left: 10px;
}

.focus-actions .ant-btn {
    padding: 0 6px;
}

.delete-btn {
    color: #f5222d;
}

.delete-btn:hover {
    color: #ff4d4f;
    background-color: rgba(245, 34, 45, 0.05);
}

.focus-images {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    margin-bottom: 15px;
}

.focus-image {
    width: 100%;
    height: 150px;
    object-fit: contain;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    cursor: pointer;
    transition: transform 0.2s;
    background-color: #f9f9f9;
}

.focus-image:hover {
    transform: scale(1.03);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.focus-info {
    border-top: 1px solid var(--border-color);
    padding-top: 10px;
}

.info-row {
    display: flex;
    margin-bottom: 5px;
}

.info-label {
    width: 100px;
    font-weight: bold;
    color: var(--text-secondary);
}

.info-value {
    flex: 1;
}

.pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}

@media (max-width: 992px) {
    .focus-grid {
        grid-template-columns: 1fr;
    }
}
</style>